import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Layers, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { useAuthStore } from "@/store/auth";
import {
  createOffice,
  lookupJstOffice,
  officeLabel,
  suggestStarostwo,
  type JstOfficeProposal,
  type JstOfficeSuggestion,
} from "../../api";
import {
  useApplicationMutations,
  useApplicationRegistry,
  useApplicationsOverview,
  useDecisions,
  useDocTemplates,
  useSelectableOffices,
  useParcels,
  useTaskPolling,
} from "../../hooks";
import type { ApplicationTypeConfig } from "../../types";
import { TaskLog } from "../ui";
import { CaseLinkPicker, type CaseLink } from "./CaseLinkPicker";
import { InfoHint } from "./InfoHint";
import { isProjectParcel } from "../../utils";

/**
 * Faza 0 — utworzenie pakietu: wybór typu procedury z rejestru + punkt zaczepienia
 * (urząd / decyzja / działka WPW / gmina / powiat). Reszta danych uzupełnia się w
 * fazach na stronie pakietu.
 */
export function CreateApplicationModal({
  connectionId,
  presetType,
  presetOffice,
  presetDecision,
  presetParcel,
  onClose,
  onBulk,
}: {
  connectionId: string;
  presetType?: string;
  presetOffice?: string;
  presetDecision?: string;
  presetParcel?: string;
  onClose: () => void;
  // Przejście do zakładania KOMPLETU wniosków (po jednym na właściciela). Kreator
  // zostaje jednoosobowy — komplet ma własny ekran z podsumowaniem, bo inaczej
  // checklisty działek per urząd zlałyby się w jedną nieczytelną listę.
  onBulk?: () => void;
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  // Czyje szablony idą na górę listy wniosków uniwersalnych — swój druk znajduje się
  // od ręki, cudzych szuka się dopiero potem.
  const currentUserId = useAuthStore((st) => st.user?.id ?? "");
  const { data: registry } = useApplicationRegistry();
  const { data: overview } = useApplicationsOverview(connectionId);
  const { data: offices } = useSelectableOffices();
  const { data: decisions } = useDecisions({ connection: connectionId });
  const { data: parcels } = useParcels(connectionId);
  // Szablony pism — wniosek uniwersalny wybiera się WŁAŚNIE nimi (rodzaj „Uniwersalny”).
  const { data: templates } = useDocTemplates();
  const { create } = useApplicationMutations();

  const [typeKey, setTypeKey] = useState(presetType ?? "");
  // Wniosek uniwersalny: wskazany szablon pisma (id) — bez niego typ nie ma treści.
  const [docTemplate, setDocTemplate] = useState("");
  const [office, setOffice] = useState(presetOffice ?? "");
  const [decision, setDecision] = useState(presetDecision ?? "");
  const [wpwParcel, setWpwParcel] = useState(presetParcel ?? "");
  // Wniosek art. 124 jest per WPW = (działka, właściciel) — współwłaściciele idą osobno.
  const [wpwOwner, setWpwOwner] = useState("");
  const [commune, setCommune] = useState("");
  const [county, setCounty] = useState("");
  // Odrolnienie: wniosek obejmuje JEDEN obręb, a do zakresu wchodzą tylko działki, na
  // których stoi obiekt naziemny na klasie chronionej. Użytkownik ma to zobaczyć i móc
  // poprawić PRZED założeniem wniosku — inaczej powstaje pusty pakiet.
  const [obreb, setObreb] = useState("");
  const [odrParcels, setOdrParcels] = useState<Set<string>>(new Set());
  const odrGroups = overview?.odrolnienie_scope ?? [];
  const odrGroup = odrGroups.find((g) => g.obreb === obreb) ?? odrGroups[0];
  useEffect(() => {
    if (typeKey !== "odrolnienie" || !odrGroup) return;
    setObreb(odrGroup.obreb);
    setOdrParcels(new Set(odrGroup.parcels.filter((p) => p.qualifies).map((p) => p.id)));
  }, [typeKey, odrGroup?.obreb]);

  // ULICP: decyzję wydaje się TAM, GDZIE NIE MA planu miejscowego, więc domyślnie
  // zaznaczamy tylko działki gminy ze stanem „brak planu”. Objęte planem, niesprawdzone
  // i „gmina nie wgrała planów” zostają odznaczone — człowiek może je dołożyć.
  const [ulicpParcels, setUlicpParcels] = useState<Set<string>>(new Set());
  const ulicpGroups = overview?.ulicp_scope ?? [];
  const ulicpGroup = ulicpGroups.find((g) => g.commune === commune);
  useEffect(() => {
    if (typeKey !== "ulicp" || !ulicpGroup) return;
    setUlicpParcels(new Set(ulicpGroup.parcels.filter((p) => p.preselected).map((p) => p.id)));
  }, [typeKey, ulicpGroup?.commune]);

  // Gmina wybiera się PIERWSZA — od niej zależy i zakres, i adresat. Projekt zwykle
  // leży w jednej gminie (95% przypadków), więc wtedy wybieramy ją same; przy kilku
  // user wskazuje jedną z tych, które faktycznie są na trasie.
  useEffect(() => {
    if (typeKey !== "ulicp" || commune) return;
    const communes = overview?.communes ?? [];
    if (communes.length === 1) setCommune(communes[0]);
  }, [typeKey, overview?.communes, commune]);

  // ── adresat gminy: rejestr → AI (jak przy wypisach i MZ) ──
  const [officeNote, setOfficeNote] = useState("");
  const [officeProposal, setOfficeProposal] = useState<JstOfficeProposal | null>(null);
  const [proposalDraft, setProposalDraft] = useState<Record<string, string>>({});
  const [lookupTaskId, setLookupTaskId] = useState<string | null>(null);
  const lookupTask = useTaskPolling(lookupTaskId);

  // Urząd gminy z rejestru: kiedy nazwa jednoznacznie wskazuje jeden wpis, wybieramy go
  // sami — dopiero brak trafienia jest powodem, żeby ruszać AI (i płacić za wyszukiwanie).
  const communeOfficeMatches = useMemo(() => {
    const needle = commune.replace(/^gmina\s+/i, "").trim().toLowerCase();
    if (!needle) return [];
    return (offices ?? []).filter(
      (o) => o.kind === "gmina" && o.name.toLowerCase().includes(needle),
    );
  }, [offices, commune]);

  useEffect(() => {
    if (typeKey !== "ulicp" || !commune || office) return;
    if (communeOfficeMatches.length === 1) {
      setOffice(communeOfficeMatches[0].id);
      setOfficeNote(`Z rejestru urzędów: ${communeOfficeMatches[0].name}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeKey, commune, communeOfficeMatches.length]);

  const applyOfficeSuggestion = (result: JstOfficeSuggestion) => {
    if (result.existing.length > 0) {
      setOffice(result.existing[0].id);
      setOfficeProposal(null);
      setOfficeNote(`Znaleziono w rejestrze: ${officeLabel(result.existing[0])}`);
    } else if (result.proposal) {
      setOfficeProposal(result.proposal);
      setProposalDraft({
        name: result.proposal.name ?? "",
        street: result.proposal.street ?? "",
        building_no: result.proposal.building_no ?? "",
        postal_code: result.proposal.postal_code ?? "",
        city: result.proposal.city ?? "",
        phone: result.proposal.phone ?? "",
        email: result.proposal.email ?? "",
        bank_account: result.proposal.bank_account ?? "",
      });
      setOfficeNote(
        result.proposal.via_web_search
          ? "Propozycja z wyszukiwania w internecie — sprawdź i popraw przed dodaniem."
          : "Propozycja z wiedzy modelu (bez wyszukiwania) — koniecznie zweryfikuj.",
      );
    } else {
      setOfficeNote("Nie udało się znaleźć urzędu — wybierz go z listy albo dodaj ręcznie.");
    }
  };

  // Ten sam obieg dla gminy (ULICP) i starostwa (odrolnienie / art. 124): najpierw
  // rejestr urzędów, a gdy pusto — wyszukiwanie w internecie w tle. Endpointy są dwa,
  // bo gmina idzie ścieżką jst_assign, a starostwo tą samą, którą zamawiamy wypisy.
  const lookupOffice = useMutation({
    mutationFn: (): Promise<JstOfficeSuggestion> => {
      if (config?.recipient_mode === "gmina") {
        return lookupJstOffice(connectionId, {
          institution: "gmina",
          commune,
          county: ulicpGroup?.county || undefined,
          voivodeship: ulicpGroup?.voivodeship || undefined,
          teryts: (ulicpGroup?.parcels ?? []).slice(0, 5).map((p) => p.teryt),
        });
      }
      const wantedCounty = county || (overview?.counties ?? [])[0] || "";
      return suggestStarostwo(wantedCounty).then((r) => ({
        existing: r.existing as JstOfficeSuggestion["existing"],
        proposal: (r.proposal ? { ...r.proposal, kind: "starostwo" } : null) as JstOfficeProposal | null,
        task_id: r.task_id,
      }));
    },
    onSuccess: (result) => {
      if (result.task_id) setLookupTaskId(result.task_id);
      else applyOfficeSuggestion(result);
    },
    onError: () => setOfficeNote("Wyszukiwanie nie powiodło się — wybierz urząd z listy."),
  });

  useEffect(() => {
    if (!lookupTaskId || !lookupTask.data) return;
    const s = lookupTask.data.status;
    if (s === "success") {
      applyOfficeSuggestion(
        (lookupTask.data.result ?? { existing: [], proposal: null }) as JstOfficeSuggestion,
      );
      setLookupTaskId(null);
    } else if (["failure", "cancelled"].includes(s)) {
      setOfficeNote("Wyszukiwanie nie powiodło się — wybierz urząd z listy.");
      setLookupTaskId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupTask.data?.status]);

  const addProposedOffice = useMutation({
    mutationFn: () =>
      createOffice({
        name: proposalDraft.name,
        kind: officeProposal?.kind || (config?.recipient_mode === "gmina" ? "gmina" : "starostwo"),
        department: officeProposal?.department ?? "",
        street: proposalDraft.street,
        building_no: proposalDraft.building_no,
        postal_code: proposalDraft.postal_code,
        city: proposalDraft.city,
        phone: proposalDraft.phone,
        email: proposalDraft.email,
        bank_account: proposalDraft.bank_account,
        notes: [
          officeProposal?.website ? `WWW: ${officeProposal.website}` : "",
          // Skąd wzięło się konto — bez tego nikt nie odróżni numeru z BIP-u od
          // wymyślonego przez model (patrz ten sam zapis przy zamówieniach MZ).
          officeProposal?.bank_account_source
            ? `Źródło numeru konta: ${officeProposal.bank_account_source}`
            : "",
          officeProposal?.source
            ? `Dodano z wyszukiwania AI (źródło: ${officeProposal.source})`
            : "Dodano z wyszukiwania AI",
        ].filter(Boolean).join("\n"),
      }),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["pp", "offices"] });
      setOffice(created.id);
      setOfficeProposal(null);
      setOfficeNote(`Dodano do rejestru i wybrano: ${created.name}`);
    },
    onError: () => setOfficeNote("Nie udało się dodać urzędu — uzupełnij nazwę i adres."),
  });

  const [selectedParcels, setSelectedParcels] = useState<Set<string>>(new Set());
  // Sprawa w Korespondencji: domyślnie wysyłka zakłada NOWĄ; „existing" podpina pakiet pod
  // otwartą sprawę projektu (opcjonalnie pod jej pozycję „wniosek" bez pakietu).
  const [caseMode, setCaseMode] = useState<"new" | "existing">("new");
  const [caseLink, setCaseLink] = useState<CaseLink | null>(null);
  const [error, setError] = useState("");
  // Komunikat o duplikacie wg reguły typu — pytanie do człowieka, nie blokada.
  const [duplicate, setDuplicate] = useState("");

  const config: ApplicationTypeConfig | undefined = useMemo(
    () => registry?.find((t) => t.key === typeKey),
    [registry, typeKey],
  );

  // Działki wybranego urzędu (wniosek lokalizacyjny) z widoku uwzględnienia — do checklisty
  // z podziałem wymaga akcji / dodatkowe.
  const officeGroup = useMemo(
    () => (config?.recipient_mode === "office_owner"
      ? (overview?.office_owners ?? []).find((o) => o.office_id === office)
      : undefined),
    [config, overview, office],
  );
  // Preselekcja: po wyborze urzędu zaznacz działki WYMAGAJĄCE akcji; dodatkowe (kontekst)
  // zostają odznaczone — user może je dołożyć ręcznie.
  useEffect(() => {
    if (!officeGroup) { setSelectedParcels(new Set()); return; }
    setSelectedParcels(new Set(
      officeGroup.parcels.filter((p) => p.inclusion.requires_action).map((p) => p.id),
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officeGroup?.office_id]);

  const toggleParcel = (id: string) =>
    setSelectedParcels((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  // Ten kreator zakłada wnioski URZĘDOWE. Pakiety WPW (do negocjatora) mają własny
  // obieg w zakładce WPW — nie oferujemy ich tu, żeby nie dublować.
  const officeTypes = useMemo(
    () => (registry ?? []).filter((t) => t.recipient_mode !== "negotiator"),
    [registry],
  );
  // Dwie sekcje wyboru: procedury NAZWANE (własny druk i własne reguły) oraz wnioski
  // uniwersalne, gdzie typem jest wskazany szablon — wybiera się je inaczej, więc
  // stoją osobno.
  const standardTypes = useMemo(
    () => officeTypes.filter((t) => !t.template_pick),
    [officeTypes],
  );
  const universalTypes = useMemo(
    () => officeTypes.filter((t) => t.template_pick),
    [officeTypes],
  );
  // Szablony do wyboru dla typu BEZ własnego druku: TYLKO rodzaju z rejestru typów
  // („Uniwersalny") — druk innej procedury wszedłby tędy bez jej reguł. Swoje na górze
  // (sort stabilny, więc w obu grupach zostaje kolejność rejestru szablonów), autor
  // w etykiecie — bo po nim też się szuka w rozwijanej liście.
  const templateOptions = useMemo(() => {
    if (!config?.template_pick) return [];
    return [...(templates ?? []).filter((tpl) => tpl.kind === config.doc_template_kind)]
      .sort((a, b) =>
        Number(b.author?.id === currentUserId) - Number(a.author?.id === currentUserId))
      .map((tpl) => ({
        value: tpl.id,
        label: `${currentUserId && tpl.author?.id === currentUserId ? "★ " : ""}`
          + `${tpl.name} — ${tpl.author?.name || "—"}`,
      }));
  }, [templates, config, currentUserId]);

  // Działki wniosku uniwersalnego — OPCJONALNE: pismo bywa o sam projekt. Zaznaczone
  // wchodzą do zmiennych #dzialki_…# pisma i do podglądu mapy zakresu.
  const [uniParcels, setUniParcels] = useState<Set<string>>(new Set());

  // Ilu właścicieli instytucjonalnych wciąż czeka na wniosek — od tego zależy, czy
  // w ogóle proponujemy zakładanie kompletu zamiast pojedynczego wniosku.
  const missingOwners = useMemo(
    () => (overview?.office_owners ?? []).filter((o) => !o.existing_application).length,
    [overview],
  );

  const officeOptions = useMemo(() => {
    if (config?.recipient_mode === "office_owner") {
      return (overview?.office_owners ?? []).map((o) => ({
        value: o.office_id,
        label: `${o.office_name} (${o.parcel_count} dz.)${o.existing_application ? " — MA JUŻ WNIOSEK" : ""}`,
      }));
    }
    const isGmina = config?.recipient_mode === "gmina";
    const kinds = config?.recipient_mode === "starostwo"
      ? ["starostwo"]
      : isGmina ? ["gmina"] : undefined;
    // Starostwo/gmina wybiera się z całej Polski, a projekt leży w konkretnych
    // powiatach/gminach — bez podpowiedzi łatwo zatwierdzić Kępno przy projekcie
    // w Gdańsku (zdarzyło się). Właściwe urzędy idą na górę listy i są oznaczone;
    // reszta zostaje dostępna.
    // Gwiazdką oznaczamy urzędy z jednostki, o którą realnie chodzi: gdy gmina jest już
    // wybrana — z NIEJ, a nie ze wszystkich gmin projektu.
    const units = (isGmina ? (commune ? [commune] : overview?.communes) : overview?.counties) ?? [];
    const projectUnits = units
      .map((c) => c.replace(/^(powiat|gmina)\s+/i, "").trim().toLowerCase())
      .filter(Boolean);
    const matches = (name: string) =>
      projectUnits.some((c) => c && name.toLowerCase().includes(c));
    const unitLabel = isGmina ? "gmina projektu" : "powiat projektu";
    const rows = (offices ?? [])
      .filter((o) => !kinds || kinds.includes(o.kind))
      .map((o) => ({
        value: o.id,
        label: kinds && matches(o.name) ? `★ ${o.name} — ${unitLabel}` : o.name,
        _match: kinds ? matches(o.name) : false,
      }));
    return rows
      .sort((a, b) => Number(b._match) - Number(a._match) || a.label.localeCompare(b.label, "pl"))
      .map(({ value, label }) => ({ value, label }));
  }, [config, overview, offices, commune]);

  // Decyzja już ostateczna nie ma o co wnioskować (backend i tak odmówi) — na liście
  // zostają nieostateczne, także te z wnioskiem w toku (drugi wniosek to pytanie o duplikat).
  const decisionOptions = useMemo(
    () => (decisions ?? [])
      .filter((d) => d.finality !== "final")
      .map((d) => ({
        value: d.id, label: `${d.sign} — ${d.kind_display} (${d.finality_display})`,
      })),
    [decisions],
  );

  // Tylko działki w grze (utils.isProjectParcel: BIEŻĄCA trasa, ręcznie dopisane, ręcznie
  // wykorzystywane). Działki zostawione przez starszą wersję trasy zostają w bazie
  // z in_current_route=False i bez tego filtra wjeżdżały do wyboru jako „działki spoza
  // projektu"; ręczne „wykorzystywana" odwrotnie — bez niego wypadało z wyboru.
  const parcelOptions = useMemo(
    () => (parcels ?? [])
      .filter((p) => isProjectParcel(p) || p.id === wpwParcel)
      .map((p) => ({ value: p.id, label: p.teryt })),
    [parcels, wpwParcel],
  );

  // Właściciele wybranej działki — prowadzący widzi, czy odmowa z WPW jest odnotowana.
  const ownerOptions = useMemo(() => {
    const parcel = (parcels ?? []).find((p) => p.id === wpwParcel);
    return (parcel?.owners ?? []).map((o) => ({
      value: o.id,
      label: o.office ? `${o.full_name} (urząd)` : o.full_name,
    }));
  }, [parcels, wpwParcel]);

  // wniosek zakotwiczony na WPW (działka + właściciel), ale adresowany do urzędu (art. 124)
  const parcelAnchored = !!config && config.unique_rule === "per_wpw"
    && config.recipient_mode !== "negotiator";

  const canSubmit = !!config && (
    (config.recipient_mode === "decision_organ" && !!decision)
    || (config.recipient_mode === "negotiator" && !!wpwParcel)
    || (config.recipient_mode === "gmina" && !!office && !!commune)
    // Wniosek uniwersalny: adresat + szablon (działki są dobrowolne).
    || (config.recipient_mode === "any_office" && !!office && !!docTemplate)
    || (["office_owner", "starostwo"].includes(config.recipient_mode) && !!office
        && (!parcelAnchored || (!!wpwParcel && !!wpwOwner)))
  ) && (caseMode === "new" || !!caseLink);

  // Wybór urzędu + wyszukiwanie AI — jeden blok dla gminy (ULICP) i starostwa
  // (odrolnienie / art. 124), bo obieg jest ten sam: rejestr → internet → poprawka
  // człowieka → wpis do rejestru.
  const officePicker = (
    <>
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <SearchableSelect
            options={officeOptions}
            value={office}
            placeholder="Wybierz urząd…"
            onChange={(e) => setOffice(e.target.value)}
          />
        </div>
        <Button
          size="sm"
          variant="secondary"
          loading={lookupOffice.isPending || !!lookupTaskId}
          title="Najpierw rejestr urzędów; gdy urzędu tam nie ma — wyszukiwanie w internecie i propozycja danych do sprawdzenia"
          onClick={() => {
            setOfficeProposal(null);
            setOfficeNote("");
            lookupOffice.mutate();
          }}
        >
          <Sparkles
            size={13}
            className={`mr-1 ${lookupOffice.isPending || lookupTaskId ? "animate-pulse" : ""}`}
          />
          Znajdź urząd (AI)
        </Button>
      </div>
      {officeNote && (
        <div className="text-[11px] text-[var(--color-text-secondary)]">{officeNote}</div>
      )}
      {lookupTaskId && <TaskLog log={lookupTask.data?.log ?? ""} />}
      {officeProposal && (
        <div className="border border-[var(--color-border)] p-2 space-y-1.5">
          <div className="text-[11px] text-[var(--color-text-secondary)] flex items-start gap-1">
            <Sparkles size={12} className="mt-0.5 shrink-0" />
            <span>
              Proponowane dane urzędu — zweryfikuj, popraw i dodaj do rejestru.
              {officeProposal.source && (
                <> Źródło: <span className="font-mono">{officeProposal.source}</span></>
              )}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {[
              ["name", "Nazwa urzędu"],
              ["street", "Ulica"],
              ["building_no", "Nr budynku"],
              ["postal_code", "Kod pocztowy"],
              ["city", "Miejscowość"],
              ["phone", "Telefon"],
              ["email", "E-mail"],
              ["bank_account", "Nr konta"],
            ].map(([field, label]) => (
              <label key={field} className="block text-[11px]">
                <span className="text-[var(--color-text-muted)]">{label}</span>
                <input
                  className="input-base w-full py-0.5 text-xs"
                  value={proposalDraft[field] ?? ""}
                  onChange={(e) => setProposalDraft((d) => ({ ...d, [field]: e.target.value }))}
                />
              </label>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              loading={addProposedOffice.isPending}
              disabled={!proposalDraft.name?.trim()}
              onClick={() => addProposedOffice.mutate()}
            >
              Dodaj do rejestru i wybierz
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setOfficeProposal(null)}>
              Odrzuć
            </Button>
          </div>
        </div>
      )}
    </>
  );

  const submit = (confirmDuplicate = false) => {
    setError("");
    if (!confirmDuplicate) setDuplicate("");
    create.mutate(
      {
        confirm_duplicate: confirmDuplicate || undefined,
        application_type: typeKey,
        connection: connectionId,
        doc_template: docTemplate || undefined,
        recipient_office: office || undefined,
        decision: decision || undefined,
        wpw_parcel: wpwParcel || undefined,
        wpw_owner: wpwOwner || undefined,
        commune: commune || undefined,
        county: county || undefined,
        // office_owner: przekaż wybrany podzbiór działek (bez wyboru backend dobierze wszystkie urzędu)
        parcel_ids: config?.recipient_mode === "office_owner" && selectedParcels.size > 0
          ? [...selectedParcels]
          : typeKey === "odrolnienie" && odrParcels.size > 0
            ? [...odrParcels]
            : typeKey === "ulicp" && ulicpParcels.size > 0
              ? [...ulicpParcels]
              : config?.recipient_mode === "any_office" && uniParcels.size > 0
                ? [...uniParcels]
                : undefined,
        obreb: typeKey === "odrolnienie" ? obreb : undefined,
        proceeding: caseMode === "existing" && caseLink ? caseLink.proceeding : undefined,
        proceeding_item: caseMode === "existing" && caseLink?.item ? caseLink.item : undefined,
      },
      {
        onSuccess: (app) => navigate(`/power-projects/wnioski/${app.id}`),
        onError: (e: unknown) => {
          const errors = (e as {
            response?: { data?: { errors?: { detail?: string; code?: string } } };
          })?.response?.data?.errors;
          const detail = errors?.detail;
          // Duplikat wg reguły typu to pytanie, nie ściana — pokazujemy je i pozwalamy
          // świadomie założyć kolejny wniosek.
          if (errors?.code === "duplicate" && typeof detail === "string") {
            setDuplicate(detail);
            return;
          }
          setError(typeof detail === "string" ? detail : "Nie udało się utworzyć wniosku.");
        },
      },
    );
  };

  return (
    <Modal open onClose={onClose} title="Nowy wniosek urzędowy" size="lg">
      <div className="space-y-4">
        <div>
          <span className="section-label flex items-center gap-1">
            Typ procedury
            <InfoHint text="Każdy typ ma własne fazy, checklistę załączników, szablon pisma i mapy oraz reguły (np. 1 urząd = 1 wniosek). Opis i instrukcję zobaczysz po wybraniu typu — oraz na stronie pakietu przy każdej fazie." />
          </span>
          <div className="mt-1 grid grid-cols-1 gap-1">
            {standardTypes.map((t) => {
              // Procedura wyłączona w rejestrze — obieg nieuzgodniony, więc zostaje na
              // liście (żeby było wiadomo, że istnieje), ale nie da się jej wybrać.
              const disabled = t.available === false;
              return (
                <label
                  key={t.key}
                  className={`flex items-start gap-2 p-2 border text-sm ${
                    disabled
                      ? "border-[var(--color-border)] opacity-60 cursor-not-allowed"
                      : typeKey === t.key
                        ? "border-[var(--color-primary)] bg-[var(--color-bg-elevated)] cursor-pointer"
                        : "border-[var(--color-border)] cursor-pointer"
                  }`}
                >
                  <input
                    type="radio"
                    className="mt-1"
                    disabled={disabled}
                    checked={typeKey === t.key}
                    onChange={() => { setTypeKey(t.key); setDocTemplate(""); }}
                  />
                  <span>
                    <span className="font-bold">{t.label}</span>
                    {disabled && (
                      <span className="ml-2 align-middle text-[10px] font-mono uppercase tracking-wider border border-[var(--color-border-strong)] text-[var(--color-text-muted)] px-1 py-0.5">
                        jeszcze niedostępne
                      </span>
                    )}
                    <span className="block text-xs text-[var(--color-text-secondary)]">{t.description}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        {/* ── Wnioski uniwersalne: własny kafelek, obok procedur nazwanych ───────────
            Pism do urzędów jest więcej niż procedur w rejestrze i nowe dochodzą bez
            przerwy. Zamiast dokładać typ w kodzie za każdym razem, zaznacza się ten
            kafelek, a druk wskazuje szablonem rodzaju „Uniwersalny" (pole niżej) —
            dalszy obieg (pełnomocnictwa, opłata, załączniki, kompilacja, wysyłka) i tak
            jest ten sam. */}
        {universalTypes.length > 0 && (
          <div>
            <span className="section-label flex items-center gap-1">
              Wnioski uniwersalne
              <InfoHint text="Pisma, które nie mają własnej procedury w systemie. Zaznacz kafelek, a potem wskaż druk — listę wypełniają WYŁĄCZNIE szablony rodzaju „Uniwersalny” z Generacji dokumentów i to wybrany szablon jest typem wniosku (jego nazwa pokazuje się przy wniosku na liście). Zakres takiego wniosku to dowolne działki projektu (nie tylko urzędowe), a adresatem może być dowolny podmiot z rejestru urzędów." />
            </span>
            <div className="mt-1 grid grid-cols-1 gap-1">
              {universalTypes.map((t) => {
                const disabled = t.available === false;
                return (
                  <label
                    key={t.key}
                    className={`flex items-start gap-2 p-2 border text-sm ${
                      disabled
                        ? "border-[var(--color-border)] opacity-60 cursor-not-allowed"
                        : typeKey === t.key
                          ? "border-[var(--color-primary)] bg-[var(--color-bg-elevated)] cursor-pointer"
                          : "border-[var(--color-border)] cursor-pointer"
                    }`}
                  >
                    <input
                      type="radio"
                      className="mt-1"
                      disabled={disabled}
                      checked={typeKey === t.key}
                      onChange={() => setTypeKey(t.key)}
                    />
                    <span>
                      <span className="font-bold">{t.label}</span>
                      <span className="block text-xs text-[var(--color-text-secondary)]">
                        {t.description}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {config && (
          <div className="space-y-3">
            {/* Kafelek zaznaczony — teraz druk. Rozwijana lista z szukaniem, bo szablonów
                bywa dużo: swoje na górze (★), autor w etykiecie, więc szuka się i po
                nazwie, i po autorze. */}
            {config.template_pick && (
              <div>
                <span className="section-label flex items-center gap-1">
                  Szablon pisma *
                  <InfoHint text="Lista zawiera WYŁĄCZNIE szablony rodzaju „Uniwersalny” z Generacji dokumentów — wybrany szablon JEST typem tego wniosku (jego nazwa pokazuje się przy wniosku na liście). Twoje szablony są na górze i oznaczone ★; szukać można po nazwie i po autorze. Nowy druk dodaje się w „Generacji dokumentów”, bez zmian w kodzie." />
                </span>
                {templateOptions.length === 0 ? (
                  <p className="mt-1 border border-[var(--color-border)] p-2 text-xs text-[var(--color-text-muted)]">
                    Brak szablonów rodzaju „Uniwersalny”. Wgraj druk w „Generacji dokumentów”
                    (rodzaj „Uniwersalny”) — pojawi się tutaj od razu.
                  </p>
                ) : (
                  <>
                    <SearchableSelect
                      name="doc-template"
                      options={templateOptions}
                      value={docTemplate}
                      placeholder="Wybierz szablon pisma…"
                      onChange={(e) => setDocTemplate(e.target.value)}
                    />
                    <p className="mt-0.5 text-[11px] text-[var(--color-text-muted)]">
                      ★ — Twoje szablony (na górze listy). Szukaj po nazwie albo po autorze.
                    </p>
                  </>
                )}
              </div>
            )}
            {config.recipient_mode === "office_owner" && missingOwners > 1 && onBulk && (
              // Skrót stąd, gdzie człowiek realnie stoi: zobaczył na liście adresatów, że
              // wniosku brakuje kilku właścicielom (np. działka ma kilku współwłaścicieli),
              // więc nie musi zakładać ich po kolei.
              <div className="flex items-center gap-2 border border-[var(--color-border)] p-2 text-xs">
                <Layers size={14} className="shrink-0 text-[var(--color-text-muted)]" />
                <span className="flex-1">
                  Wniosku nie ma jeszcze {missingOwners} właścicieli — możesz założyć
                  komplet za jednym razem, po jednym wniosku na każdego.
                </span>
                <Button size="sm" variant="secondary" onClick={onBulk}>
                  Załóż wszystkie
                </Button>
              </div>
            )}
            {config.recipient_mode === "office_owner" && (
              <label className="block">
                <span className="section-label flex items-center gap-1">
                  Adresat (urząd) *
                  <InfoHint text="Lista pokazuje urzędy będące właścicielami działek w projekcie (właściciele instytucjonalni z podpiętym urzędem). Działki urzędu podepną się automatycznie. Gdy wniosku brakuje kilku właścicielom — np. działka ma kilku współwłaścicieli — załóż komplet jednym kliknięciem." />
                </span>
                <SearchableSelect
                  options={officeOptions}
                  value={office}
                  placeholder="Wybierz urząd…"
                  onChange={(e) => setOffice(e.target.value)}
                />
              </label>
            )}
            {config.recipient_mode === "starostwo" && (
              <div className="space-y-1">
                <span className="section-label flex items-center gap-1">
                  Adresat (starostwo) *
                  <InfoHint text="Starostwo/prezydent miasta właściwy dla działek. „Znajdź urząd (AI)” szuka najpierw w rejestrze urzędów, a gdy go tam nie ma — w internecie; propozycję poprawiasz i dodajesz do rejestru jednym kliknięciem." />
                </span>
                {officePicker}
              </div>
            )}
            {/* Wniosek uniwersalny: adresatem bywa dowolna instytucja (konserwator,
                wojewoda, zarząd dróg), więc lista NIE jest zawężana rodzajem urzędu,
                a działki są dobrowolne — pismo bywa o sam projekt. */}
            {config.recipient_mode === "any_office" && (
              <>
                <div>
                  <span className="section-label flex items-center gap-1">
                    Działki wniosku ({uniParcels.size})
                    <InfoHint text="Opcjonalne: zaznacz działki, których pismo dotyczy — wejdą do zmiennych #dzialki_…# szablonu i do podglądu mapy zakresu. Pismo o sam projekt (zapytanie, uzupełnienie sprawy) zostaw bez działek. Zakres zmienisz też później na stronie pakietu." />
                  </span>
                  {parcelOptions.length === 0 ? (
                    <p className="mt-1 text-xs text-[var(--color-text-muted)]">
                      Projekt nie ma jeszcze działek z bieżącej trasy.
                    </p>
                  ) : (
                    <div className="mt-1 max-h-40 overflow-auto border border-[var(--color-border)]">
                      {parcelOptions.map((p) => (
                        <label
                          key={p.value}
                          className="flex items-center gap-2 px-2 py-1 text-xs cursor-pointer border-b border-[var(--color-border)] last:border-b-0"
                        >
                          <input
                            type="checkbox"
                            checked={uniParcels.has(p.value)}
                            onChange={() => {
                              const next = new Set(uniParcels);
                              if (next.has(p.value)) next.delete(p.value);
                              else next.add(p.value);
                              setUniParcels(next);
                            }}
                          />
                          <span className="font-mono">{p.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <label className="block">
                  <span className="section-label flex items-center gap-1">
                    Adresat (urząd) *
                    <InfoHint text="Cały rejestr urzędów — bez zawężania rodzajem, bo pismo uniwersalne bywa adresowane gdziekolwiek (konserwator zabytków, wojewoda, zarząd dróg, spółka wodna). Brakującego adresata dodasz w rejestrze urzędów; adresata można też zmienić później w edycji wniosku." />
                  </span>
                  <SearchableSelect
                    name="recipient-office"
                    options={officeOptions}
                    value={office}
                    placeholder="Wybierz urząd…"
                    onChange={(e) => setOffice(e.target.value)}
                  />
                </label>
              </>
            )}
            {officeGroup && (
              <div className="border border-[var(--color-border)] p-2 space-y-1.5">
                <div className="section-label flex items-center gap-1">
                  Działki w pakiecie
                  <InfoHint text="Domyślnie zaznaczone są działki wymagające akcji (tryb dostępu inny niż „nie wymaga”). Działki dodatkowe (kontekst) możesz dołożyć — wejdą do zakresu i map wniosku. Zakres zmienisz też później na stronie pakietu." />
                </div>
                {(() => {
                  const action = officeGroup.parcels.filter((p) => p.inclusion.state === "action");
                  // działki, na których wisi już ZŁOŻONY wniosek — czekamy na urząd, więc nie
                  // są naszym ruchem; pokazujemy je osobno, żeby nie dublować zakresu bez wiedzy
                  const awaiting = officeGroup.parcels.filter(
                    (p) => p.inclusion.state === "awaiting_response" || p.inclusion.state === "kpa_overdue");
                  // działki, na które urząd już odpowiedział pozytywnie (tytuł dostępu jest) —
                  // nie preselekcjonujemy ich, ale można dołożyć jak kontekst
                  const secured = officeGroup.parcels.filter(
                    (p) => p.inclusion.state === "secured");
                  const additional = officeGroup.parcels.filter(
                    (p) => p.inclusion.state === "additional");
                  const renderParcel = (p: { id: string; teryt: string; inclusion: { access_mode_label: string } }) => (
                    <label key={p.id} className="flex items-center gap-1.5 text-xs cursor-pointer py-0.5">
                      <input type="checkbox" checked={selectedParcels.has(p.id)} onChange={() => toggleParcel(p.id)} />
                      <span className="font-mono">{p.teryt}</span>
                      <span className="text-[10px] text-[var(--color-text-muted)]">· {p.inclusion.access_mode_label}</span>
                    </label>
                  );
                  return (
                    <>
                      {action.length > 0 && (
                        <div>
                          <div className="text-[10px] font-mono uppercase tracking-wide text-amber-700 dark:text-amber-400 mb-0.5">
                            Wymaga akcji ({action.length})
                          </div>
                          {action.map(renderParcel)}
                        </div>
                      )}
                      {awaiting.length > 0 && (
                        <div className="pt-1 border-t border-[var(--color-border-muted)]">
                          <div className="text-[10px] font-mono uppercase tracking-wide text-sky-700 dark:text-sky-400 mb-0.5">
                            Oczekiwanie na odpowiedź — wniosek złożony ({awaiting.length})
                          </div>
                          {awaiting.map(renderParcel)}
                        </div>
                      )}
                      {secured.length > 0 && (
                        <div className="pt-1 border-t border-[var(--color-border-muted)]">
                          <div className="text-[10px] font-mono uppercase tracking-wide text-emerald-700 dark:text-emerald-400 mb-0.5">
                            Pozyskane — tytuł dostępu już jest ({secured.length})
                          </div>
                          {secured.map(renderParcel)}
                        </div>
                      )}
                      {additional.length > 0 && (
                        <div className="pt-1 border-t border-[var(--color-border-muted)]">
                          <div className="text-[10px] font-mono uppercase tracking-wide text-[var(--color-text-muted)] mb-0.5">
                            Dodatkowe — kontekst ({additional.length})
                          </div>
                          {additional.map(renderParcel)}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
            {config.recipient_mode === "gmina" && (
              <>
                <label className="block">
                  <span className="section-label flex items-center gap-1">
                    Gmina *
                    <InfoHint text="Od gminy zaczyna się wszystko: wyznacza zakres (jej działki bez planu miejscowego) i adresata. Lista to gminy działek z bieżącej trasy — gdy projekt leży w jednej, jest wybrana od razu. Reguła: 1 gmina = 1 wniosek ULICP na projekcie." />
                  </span>
                  <SearchableSelect
                    options={(overview?.communes ?? []).map((g) => ({ value: g, label: g }))}
                    value={commune}
                    placeholder="Wybierz gminę…"
                    onChange={(e) => {
                      setCommune(e.target.value);
                      setOffice("");
                      setOfficeProposal(null);
                      setOfficeNote("");
                    }}
                  />
                  {(overview?.communes ?? []).length === 1 && (
                    <span className="text-[11px] text-[var(--color-text-muted)]">
                      Projekt leży w jednej gminie — wybrana automatycznie.
                    </span>
                  )}
                </label>
                {commune && (
                  <div className="space-y-1">
                    <span className="section-label flex items-center gap-1">
                      Adresat (urząd gminy) *
                      <InfoHint text="Najpierw rejestr urzędów: gdy jest tam dokładnie jeden urząd tej gminy, wybieramy go sam. Gdy go nie ma — „Znajdź urząd (AI)” szuka najpierw w rejestrze, a potem w internecie i podsuwa dane do sprawdzenia; po poprawieniu dodajesz go do rejestru jednym kliknięciem." />
                    </span>
                    {officePicker}
                  </div>
                )}
              </>
            )}
            {config.key === "ulicp" && ulicpGroup && (
              <div>
                <span className="section-label flex items-center gap-1">
                  Działki wniosku ({ulicpGroup.preselected_count}/{ulicpGroup.parcels.length}
                  {" "}bez planu)
                  <InfoHint text="Decyzję o ustaleniu lokalizacji inwestycji celu publicznego wydaje się TAM, GDZIE NIE MA planu miejscowego — dlatego zaznaczone są tylko działki ze stanem „brak planu”. Działki objęte planem, niesprawdzone i te z gmin, które nie wgrały planów do rejestru krajowego, zostają odznaczone: żaden z tych stanów nie znaczy, że planu nie ma. Zaznaczenie możesz zmienić — bywa plan, który nie obejmuje inwestycji celu publicznego. Pokrycie przeliczysz przyciskiem „Pobierz pokrycie MPZP” na zakładce Działki." />
                </span>
                <div className="mt-1 max-h-56 overflow-auto border border-[var(--color-border)]">
                  {ulicpGroup.parcels.map((p) => (
                    <label
                      key={p.id}
                      className="flex items-start gap-2 px-2 py-1 text-xs border-b border-[var(--color-border)] last:border-b-0"
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={ulicpParcels.has(p.id)}
                        onChange={() => {
                          const next = new Set(ulicpParcels);
                          if (next.has(p.id)) next.delete(p.id);
                          else next.add(p.id);
                          setUlicpParcels(next);
                        }}
                      />
                      <span className="flex-1">
                        <span className="font-mono">{p.short_number || p.teryt}</span>
                        {p.obreb && (
                          <span className="ml-2 text-[var(--color-text-muted)]">obręb {p.obreb}</span>
                        )}
                        <span className="block text-[var(--color-text-muted)]">
                          {p.mpzp_status_label}
                        </span>
                      </span>
                      {p.mpzp_status === "covered" && <Badge color="red">objęta planem</Badge>}
                      {(p.mpzp_status === "unchecked" || p.mpzp_status === "unknown") && (
                        <Badge color="yellow">do sprawdzenia</Badge>
                      )}
                    </label>
                  ))}
                </div>
                {ulicpGroup.unchecked_count > 0 && (
                  <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                    {ulicpGroup.unchecked_count} działek bez potwierdzonego pokrycia planem —
                    przelicz je na zakładce Działki („Pobierz pokrycie MPZP”), zanim uznasz, że
                    planu nie ma.
                  </p>
                )}
                {ulicpGroup.preselected_count === 0 && (
                  <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                    W tej gminie żadna działka nie ma potwierdzonego braku planu miejscowego —
                    ULICP wydaje się tylko w braku planu.
                  </p>
                )}
              </div>
            )}
            {config.key === "odrolnienie" && odrGroups.length > 0 && (
              <div className="col-span-2">
                <span className="section-label flex items-center gap-1">
                  Obręb i działki wniosku
                  <InfoHint text="Jeden wniosek obejmuje jeden obręb — tak zbudowany jest formularz (jeden adres nieruchomości). Zaznaczone są działki, które kwalifikuje automat: obiekt naziemny (stacja/złącze/słup) na gruncie klasy chronionej I–III. Sam kabel pod ziemią nie wymaga wyłączenia." />
                </span>
                <div className="mt-1 flex flex-wrap gap-1">
                  {odrGroups.map((g) => (
                    <button
                      key={g.obreb}
                      type="button"
                      onClick={() => setObreb(g.obreb)}
                      className={`px-2 py-1 text-xs border ${
                        g.obreb === (odrGroup?.obreb ?? "")
                          ? "border-[var(--color-primary)] bg-[var(--color-primary)] text-[var(--color-text-inverse)]"
                          : "border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]"
                      }`}
                    >
                      obręb {g.obreb_label} · {g.qualifying}/{g.parcels.length}
                    </button>
                  ))}
                </div>
                {odrGroup && (
                  <div className="mt-2 max-h-56 overflow-auto border border-[var(--color-border)]">
                    {odrGroup.parcels.map((p) => (
                      <label
                        key={p.id}
                        className="flex items-start gap-2 px-2 py-1 text-xs border-b border-[var(--color-border)] last:border-b-0"
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5"
                          checked={odrParcels.has(p.id)}
                          onChange={() => {
                            const next = new Set(odrParcels);
                            if (next.has(p.id)) next.delete(p.id);
                            else next.add(p.id);
                            setOdrParcels(next);
                          }}
                        />
                        <span className="flex-1">
                          <span className="font-mono">{p.short_number || p.teryt}</span>
                          {p.exclusion_area_m2 > 0 && (
                            <span className="ml-2 font-mono text-[var(--color-text-muted)]">
                              {p.exclusion_area_m2} m²
                            </span>
                          )}
                          <span className="block text-[var(--color-text-muted)]">{p.reason}</span>
                        </span>
                        {p.required && <Badge color="red">wymaga</Badge>}
                        {p.manual && !p.required && <Badge color="yellow">do decyzji</Badge>}
                      </label>
                    ))}
                  </div>
                )}
                {odrGroup && odrGroup.qualifying === 0 && (
                  <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                    W tym obrębie żadna działka nie wymaga wyłączenia — sam kabel pod ziemią nie
                    zajmuje trwale gruntu. Zaznacz działki ręcznie tylko wtedy, gdy wiesz lepiej.
                  </p>
                )}
              </div>
            )}
            {config.key === "odrolnienie" && (
              <label className="block">
                <span className="section-label flex items-center gap-1">
                  Powiat
                  <InfoHint text="Powiat, którego starosta wyda zezwolenie. Podpowiedzi z powiatów działek projektu." />
                </span>
                <SearchableSelect
                  options={(overview?.counties ?? []).map((p) => ({ value: p, label: p }))}
                  value={county}
                  placeholder="Wybierz powiat…"
                  onChange={(e) => setCounty(e.target.value)}
                />
              </label>
            )}
            {config.recipient_mode === "decision_organ" && (
              <label className="block">
                <span className="section-label flex items-center gap-1">
                  Decyzja z rejestru *
                  <InfoHint text="Wybierz decyzję, którą chcesz oklauzulować. Adresat (organ wydający) ustawi się sam. Brak decyzji na liście? Zarejestruj ją w panelu „Rejestr decyzji” na zakładce Wnioski." />
                </span>
                <SearchableSelect
                  options={decisionOptions}
                  value={decision}
                  placeholder="Wybierz decyzję…"
                  onChange={(e) => setDecision(e.target.value)}
                />
              </label>
            )}
            {parcelAnchored && (
              <>
                <label className="block">
                  <span className="section-label flex items-center gap-1">
                    Działka wniosku *
                    <InfoHint text="1 WPW (działka + właściciel) = 1 wniosek art. 124 — tak samo dla ENERGA i TAURON. Każdy współwłaściciel, który odmówił, to OSOBNY wniosek, z własnym numerem, opłatą i decyzją. Powód (brak zgody po rokowaniach / nieuregulowany stan prawny / brak kontaktu ze współwłaścicielem) opisujesz w polu „Opis rokowań” na wniosku." />
                  </span>
                  <SearchableSelect
                    options={parcelOptions}
                    value={wpwParcel}
                    placeholder="Wybierz działkę…"
                    onChange={(e) => { setWpwParcel(e.target.value); setWpwOwner(""); }}
                  />
                </label>
                <label className="block">
                  <span className="section-label flex items-center gap-1">
                    Właściciel (WPW) *
                    <InfoHint text="Wniosek dotyczy jednego właściciela — jego dane trafią do pisma, a dokumentacją rokowań będzie WYŁĄCZNIE jego odmowa z WPW. Współwłaścicieli obsługujesz osobnymi wnioskami." />
                  </span>
                  <SearchableSelect
                    options={ownerOptions}
                    value={wpwOwner}
                    placeholder={wpwParcel ? "Wybierz właściciela…" : "Najpierw wybierz działkę"}
                    onChange={(e) => setWpwOwner(e.target.value)}
                  />
                  {wpwParcel && ownerOptions.length === 0 && (
                    <span className="text-[11px] text-amber-600">
                      Ta działka nie ma jeszcze wpisanych właścicieli — uzupełnij ich na działce.
                    </span>
                  )}
                </label>
              </>
            )}
            {config.recipient_mode === "negotiator" && (
              <label className="block">
                <span className="section-label flex items-center gap-1">
                  Działka WPW *
                  <InfoHint text="1 działka = 1 pakiet. Pakiet obejmie właścicieli tej działki objętych zleceniem WPW, a po kompilacji trafi do teczek negocjatora." />
                </span>
                <SearchableSelect
                  options={parcelOptions}
                  value={wpwParcel}
                  placeholder="Wybierz działkę…"
                  onChange={(e) => setWpwParcel(e.target.value)}
                />
              </label>
            )}
            <div className="text-xs text-[var(--color-text-secondary)] border-l-2 border-[var(--color-border-strong)] pl-2">
              {config.instructions}
            </div>
          </div>
        )}

        {config && config.recipient_mode !== "negotiator" && (
          <div className="space-y-2">
            <span className="section-label flex items-center gap-1">
              Sprawa w Korespondencji
              <InfoHint text="Wysyłka pakietu prowadzi sprawę w zakładce Sprawy projektu. Domyślnie zakłada nową. Jeśli z tym urzędem sprawa już się toczy (np. wniosek wysłano z Korespondencji), podepnij pakiet pod nią — a gdy ma pozycję „Wniosek” bez pakietu, zapytamy, czy podpiąć pakiet do tej pozycji zamiast dokładać drugą. Tylko sprawy tego projektu." />
            </span>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="radio" name="case-mode" checked={caseMode === "new"}
                  onChange={() => { setCaseMode("new"); setCaseLink(null); }} />
                Nowa sprawa (założy się przy wysyłce)
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="radio" name="case-mode" checked={caseMode === "existing"}
                  onChange={() => setCaseMode("existing")} />
                Podepnij do istniejącej sprawy projektu
              </label>
            </div>
            {caseMode === "existing" && (
              <CaseLinkPicker
                connectionId={connectionId}
                recipientOffice={office || null}
                value={caseLink}
                onChange={setCaseLink}
              />
            )}
          </div>
        )}

        {error && <div className="text-xs text-red-600">{error}</div>}
        {/* Duplikat wg reguły typu: pytamy, zamiast blokować. Pierwszy wniosek bywa
            błędnym szkicem, a urząd potrafi kazać podzielić zakres — decyzja należy
            do prowadzącego sprawę. */}
        {duplicate && (
          <div className="border border-amber-300 dark:border-amber-500/40 bg-amber-50/60 dark:bg-amber-500/10 p-2 space-y-1.5">
            <p className="flex items-start gap-1.5 text-amber-700 dark:text-amber-400 text-xs">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {duplicate}
            </p>
            <p className="text-[11px] text-amber-700/80 dark:text-amber-400/80">
              Jeśli wiesz, co robisz (pierwszy wniosek to nieaktualny szkic, urząd kazał
              podzielić zakres) — załóż kolejny.
            </p>
            <div className="flex items-center gap-2">
              <Button size="sm" loading={create.isPending} onClick={() => submit(true)}>
                Załóż mimo to
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setDuplicate("")}>
                Wróć do edycji
              </Button>
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Anuluj</Button>
          <Button onClick={() => submit()} loading={create.isPending} disabled={!canSubmit}>
            Utwórz pakiet
          </Button>
        </div>
      </div>
    </Modal>
  );
}
